import * as vp from 'vopay';
import { useEffect, useState } from 'react';
import axios from 'axios';

import { useDataFetchIndicator } from '..';

import vopayApi from '../../services/api.config';

type HookProps = { api: vp.Api; bearer?: string };

export type PartnerAccountStatusFilter = vp.PartnerAccountStatus | '' | 'all';
export type PartnerAccountsRequest = Omit<vp.PartnerAccountsRequest, 'VopayAccountStatus'> & {
  VopayAccountStatus?: PartnerAccountStatusFilter;
};
type HookTuple = [
  response: FetchPartnerAccountsResponse | undefined,
  fetchData: (params?: PartnerAccountsRequest) => void,
  reset: () => void,
  fetchDataAsync: (params?: vp.PartnerAccountsRequest) => Promise<FetchPartnerAccountsResponse>,
];

type ApiHook = (props: HookProps) => HookTuple;

export type FetchPartnerAccountsResponse = {
  success: boolean;
  errorMessage: string;
  accountDetails: vp.AccountDetailsMap;
  balances: vp.AccountBalance[];
  permissions: vp.AccountPermissionsMap;
  CSVFile?: string;
  PaginationMetaData: vp.PaginationMetaData;
};

const useFetchPartnerAccounts: ApiHook = ({ api, bearer }) => {
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [params, setParams] = useState<PartnerAccountsRequest>();
  const [response, setResponse] = useState<FetchPartnerAccountsResponse>();
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const source = axios.CancelToken.source();

  useEffect(() => {
    if (!shouldFetchData) return () => {};

    const loadData = async () => {
      setDataFetchingInfo(true, 'Loading Data', 'fullScreen');

      const { fromAccounts } = vp.dataTransformers;

      const { Success, ErrorMessage, Accounts, CSVFile, PaginationMetaData } = await api.fetchPartnerAccounts(
        params
          ? {
              ...params,
              ShowToken: true,
              ...(params.ExportCSV ? {} : { Limit: params.Limit || 20, Offset: params.Offset || 0 }),
              Country: params.Country === 'all' ? '' : params.Country,
              Currency: params.Currency === 'all' ? '' : params.Currency,
              VopayAccountStatus:
                params.VopayAccountStatus === 'all' || params.VopayAccountStatus == ''
                  ? undefined
                  : params.VopayAccountStatus,
            }
          : {},
        source,
        bearer,
      );

      const { toAccountDetails, toBalances, toPermissions } = fromAccounts(Accounts);

      setResponse({
        success: Success,
        errorMessage: ErrorMessage,
        accountDetails: toAccountDetails,
        balances: toBalances,
        permissions: toPermissions,
        CSVFile: CSVFile,
        PaginationMetaData: PaginationMetaData,
      });

      setDataFetchingInfo(false);
    };

    if (shouldFetchData) void loadData();
    setShouldFetchData(false);

    return () => {
      source.cancel();
    };
  }, [shouldFetchData]);

  const fetchPartnerPortalAsync = async (
    requestParams?: vp.PartnerAccountsRequest,
  ): Promise<FetchPartnerAccountsResponse> => {
    const { fromAccounts } = vp.dataTransformers;

    const { Success, ErrorMessage, Accounts, CSVFile, PaginationMetaData } = await api.fetchPartnerAccounts(
      { ...requestParams, ShowToken: true },
      source,
      bearer,
    );

    const { toAccountDetails, toBalances, toPermissions } = fromAccounts(Accounts);

    return {
      success: Success,
      errorMessage: ErrorMessage,
      accountDetails: toAccountDetails,
      balances: toBalances,
      permissions: toPermissions,
      CSVFile: CSVFile,
      PaginationMetaData: PaginationMetaData,
    };
  };

  return [
    response,
    (_params?: PartnerAccountsRequest) => {
      setShouldFetchData(true);
      setParams(_params);
    },
    () => {
      setResponse(undefined);
      setShouldFetchData(false);
    },
    fetchPartnerPortalAsync,
  ];
};

export default vopayApi.withApiHook<HookProps, HookTuple>(useFetchPartnerAccounts);
